import React, { useState, useEffect } from 'react';
import './IssuesList.css'
import { DataGrid, GridColDef, GridSortModel, GridToolbar, } from '@mui/x-data-grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import moment from 'moment';
import { Avatar, Box, Divider, IconButton, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useRootStore } from '../hooks/useRootStore';
import { getIssuePriorityNameRu } from '../data/IssuePriority';
import FlagIcon from '@mui/icons-material/Flag';
import IssueListFilter from './IssueListFilter';
import { getIssueStateNameRu, IssueState } from '../data/IssueState';
import { Skill } from '../data/Skill';
import { Client } from '../data/Client';
import { getPersonInitials, Person } from '../data/Person';
import { Project } from '../data/Project';
import { Range } from '../data/Range';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { UserType } from '../data/UserType';
import useDeviceMedia from '../hooks/useDeviceMedia';

export type IssueListItem = {
    ID: number,
    Subject: string,
    CreationDate: string,
    AuthorID: number,
    StateID: number,
    ProjectID: number,
    ProjectName: string,
    PriorityID: number,
    ClientID: 0,
    ClientName: string,
    SkillID?: number,
    SkillName: string,
    Author: {
        ID: number,
        Name: string,
        Avatar: string,
        LastName: string,
        FirstName: string,
    },
    AssignedEmployee?: {
        ID: number,
        Name: string,
        Avatar: string,
        LastName: string,
        FirstName: string,
    },
    AuthorName: string,
    AuthorAvatar: string,
    EstimatedDate: string,
    EstimationHours?: number,
    AssignedToEmployeeAgentID?: number,
    AssignedToEmployeeAgentName: string,
    AssignedToEmployeeAgentAvatar: string,
    AssignedDate?: string,
    AcceptanceDate?: Date,
    QueuedDate?: Date,
    ScoreName: string,
    ScoreID?: number,
    TrackedHours: number,
    IsError: boolean,
}

export type IssueListFilterModel = {
    states: IssueState[],
    isError: boolean,
    skills: Skill[],
    projects: Project[],
    clients: Client[],
    authors: Person[],
    assignees: Person[],
    rates: Range<number>,
    hasFact?: boolean,
    datesCreate: Range<Date>,
    datesQueued: Range<Date>,
    datesCompleted: Range<Date>,
    datesAcceptance: Range<Date>,
    datesAssigned: Range<Date>,
    datesDeadline: Range<Date>,
    myErrors?: boolean,
    meMode?: boolean,
    text?: string,

    offset?: number,

    orderBy?: string,
}

const IssuesList = observer(() => {
    const { api, userProfile } = useRootStore();
    const [list, setList] = useState<IssueListItem[]>([]);
    const [recordsCount, setRecordsCount] = useState(-1);
    const { isMobile, isMobilePortrait } = useDeviceMedia();

    const [searchText, setSearchText] = useState('');

    const theme = useTheme();

    const [filterModel, setFilterModel] = useState(
        {
            /*assignees: new Map<number, Person>(),
            authors: new Map<number, Person>(),
            clients: new Map<number, Client>(),
            states: [],
            datesCreate: { from: null, to: null } as Range<Date>,
            datesAssigned: { from: null, to: null } as Range<Date>,
            datesCompleted: { from: null, to: null } as Range<Date>,
            datesAccepted: { from: null, to: null } as Range<Date>,
            datesDeadline: { from: null, to: null } as Range<Date>,
            projects: new Map<number, Project>(),
            pageNumber: 0,
            rates: { from: 0, to: 10 } as Range<number>,
            skills: [],
            hasFact: false,*/
            meMode: true
        } as IssueListFilterModel);

    //const [loading, setLoading] = useState(false);

    useEffect(() => {
        setRecordsCount(-1);
        setList([]);
        api.getIssues(filterModel).then(response => {
            setRecordsCount(response.TotalRecordsCount);
            setList(response.Records);
        });
    }, [api, filterModel]);

    const columns: GridColDef[] = [        
        {
            field: 'StateIdIcon', cellClassName: (params) => `grid-column-icon center state-${params.row.StateID}`, headerName: '', width: 30, renderCell: (params) =>
                <Tooltip title={getIssueStateNameRu(params.row.StateID)} placement='right-end' arrow disableInteractive>
                    <Box>&nbsp;</Box>
                </Tooltip>
        },
        {
            field: 'PriorityID', cellClassName: (params) => `issue-priority priority-${params.row.PriorityID}`, headerName: '', width: 20, renderCell: (params) =>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems:'center', height:'100%' }}>
                    <Tooltip
                        title={getIssuePriorityNameRu(params.value)}
                        disableInteractive
                        placement='left'>
                        <FlagIcon />
                    </Tooltip>
                </Box>
        },
        {
            field: 'IsError', headerName: 'Ошибка', width: 100, align:'center', valueFormatter: (value: boolean) => value ? 'да' : 'нет'
        },
        { field: 'ID', headerName: 'ID', align: 'center', headerAlign: 'center', width: 90, renderCell: (params) => <Link href={`./issues/${params.id}`}>{params.id}</Link> },
        { field: 'CreationDate', headerName: 'Создана', align: 'center', width: 125, type: 'date', valueFormatter: (value?: Date) => value ? moment(value).format("DD.MM.YYYY") : null },
        { field: 'StateID', cellClassName: (params) => `grid-column-icon state-${params.row.StateID}`, headerName: 'Статус', width: 130, renderCell: (params) => getIssueStateNameRu(params.row.StateID) },
        {
            field: 'AssignedToEmployeeAgentID', headerName: 'Исполнитель', width: 160, renderCell: (params) =>
                params.row.AssignedEmployee?.ID ?
                    <Link sx={{ display: 'flex', alignItems: 'center' }} href={`./employees/${params.row.AssignedEmployee.ID}`}>
                        <Avatar
                            src={params.row.AssignedEmployee.Avatar}
                            sx={{
                                border: 'none', fontWeight: 'bold', backgroundColor: 'orange', mr: 1, width: '30px', height: '30px', lineHeight: '30px',
                                fontSize: 12,
                                textTransform: 'uppercase'
                            }}>
                            {getPersonInitials({ LastName: params.row.AssignedEmployee.Name.split(' ')[0], FirstName: params.row.AssignedEmployee.Name.split(' ')[1] } as Person)}
                        </Avatar>
                        {params.row.AssignedEmployee.Name}
                    </Link>
                    : ''
        },
        {
            field: 'AssignedToEmployeeAgentAvatar', headerName: '', width: 36, renderCell: (params) =>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height:'100%' }}>
                    {params.row.AssignedEmployee?.ID ?
                        <Tooltip title={`Исполнитель: ${params.row.AssignedEmployee.Name}`} placement='right' disableInteractive >
                            <Link href={`./employees/${params.row.AssignedEmployee.ID}`}>
                                <Avatar
                                    src={params.row.AssignedEmployee.Avatar}
                                    sx={{
                                        border: 'none', fontWeight: 'bold', backgroundColor: 'orange', width: '30px', height: '30px', lineHeight: '30px',
                                        fontSize: 12,
                                        textTransform: 'uppercase'
                                    }}>
                                    {getPersonInitials({ LastName: params.row.AssignedEmployee.Name.split(' ')[0], FirstName: params.row.AssignedEmployee.Name.split(' ')[1] } as Person)}
                                </Avatar>
                            </Link>
                        </Tooltip>
                        :
                        <Tooltip title="исполнитель не назначен" placement='right' disableInteractive >
                            <Avatar sx={{ width: '30px', height: '30px', lineHeight: '30px', fontSize: 12, textTransform: 'uppercase' }} />
                        </Tooltip>
                    }
                    </Box>
        },
        { field: 'Subject', headerName: 'Задача', width: 250, renderCell: (params) => <Link href={`./issues/${params.id}`}>{params.value}</Link> },
        { field: 'AssignedDate', type: 'dateTime', headerName: 'Назначена', align: 'center', width: 125, valueFormatter: (value?: Date) => value ? moment(value).format("DD.MM.YYYY") : null },
        { field: 'AcceptanceDate', type: 'dateTime', headerName: 'Принять до', align: 'center', width: 125, valueFormatter: (value?: Date) => value ? moment(value).format("DD.MM.YYYY") : null },
        { field: 'QueuedDate', type: 'dateTime', headerName: 'Поставлена в очередь', align: 'center', width: 125, valueFormatter: (value?: Date) => value ? moment(value).format("DD.MM.YYYY") : null },
        { field: 'EstimatedDate', headerName: 'Дедлайн', align: 'center', width: 115, valueFormatter: (value?: Date) => value ? moment(value).format("DD.MM.YYYY") : null },
        { field: 'SkillName', headerName: 'Направление', width: 200, renderCell: (params) => params.row.SkillName },
        { field: 'ProjectName', headerName: 'Проект', width: 130, renderCell: (params) => <Link href={`./projects/${params.row.ProjectID}`}>{params.value}</Link> },
        { field: 'ScoreID', headerName: 'Оценка', width: 85, align: 'center', renderCell: (params) => <Tooltip title={params.row.ScoreName}><span>{params.value ? params.value / 2 : ''}</span></Tooltip> }, //valueGetter: (value?: number) => value ? value / 2 : ''},
        { field: 'EstimationHours', headerName: 'План', width: 90, align: 'right', valueFormatter: (value?: number) => value ? moment.duration(value * 60 * 60 * 1000).format("hh:mm", { trim: false }) : '' },
        { field: 'TrackedHours', headerName: 'Факт', width: 90, align: 'right', valueFormatter: (value: number) => moment.duration(value * 60 * 60 * 1000).format("hh:mm", { trim: false }) },
        { field: 'AuthorName', headerName: 'Заявитель', width: 250, renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center' }}><Avatar src={params.row.AuthorAvatar} sx={{ mr: 1, width: '30px', height: '30px' }} /><Link href={`./employees/${params.row.AuthorID}`}>{params.value}</Link></Box> },
        {
            field: 'AuthorAvatar', headerName: '', width: 36, renderCell: (params) =>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    <Tooltip title={`Автор: ${params.row.AuthorName}`} placement='right' disableInteractive >
                        <Link href={`./employees/${params.row.AuthorID}`}>
                            <Avatar
                                src={params.row.AuthorAvatar}
                                sx={{
                                    border: 'none', fontWeight: 'bold', backgroundColor: 'orange', width: '30px', height: '30px', lineHeight: '30px',
                                    fontSize: 12,
                                    textTransform: 'uppercase'
                                }}>
                                {getPersonInitials({ LastName: params.row.AuthorName.split(' ')[0], FirstName: params.row.AuthorName.split(' ')[1] } as Person)}
                            </Avatar>
                        </Link>
                    </Tooltip>
                </Box>
        },
    ];

    if (userProfile?.UserType == UserType.Employee) {
        columns.splice(columns.length-2, 0, { field: 'ClientName', headerName: 'Клиент', width: 180, renderCell: (params) => <Link href={`./clients/${params.row.ClientID}`}>{params.value}</Link> });
    }

    const paginationModel = { page: 0, pageSize: 100 };

    const [pressedButtons, setPressedButtons] = useState<(IssueState | string)[]>(['all']);

    const [filterShowing, setFilterShowing] = useState(false);
    
    const handleBtnClick = (
        event: React.MouseEvent<HTMLElement>,
        newStates: (IssueState | string)[],
    ) => {
        if (event.currentTarget.outerText /*target.value*/ == 'ВСЕ') {
            newStates = [];
            setPressedButtons(['all']);
        }
        else {
            newStates = newStates.filter(x => x != 'all');
            setPressedButtons(newStates);
        }

        setFilterModel({ ...filterModel, states: newStates.filter(x => x != 'all') as IssueState[] })
    };

    const handleSortModelChange = (model: GridSortModel) => {
        if (model.length > 0) {
            //let sortFieldName = '';
            if (model[0].field == 'StateIdIcon') {
                model[0].field = 'StateID';
            }
            if (model[0].field == 'AssignedToEmployeeAgentID') {
                model[0].field = 'AssignedToEmployeeAgentName';
            }
            /*switch (model[0].field) {
                case 'StateIdIcon': sortFieldName = 'StateID'; break;
                case 'PriorityID': sortFieldName = 'PriorityID'; break;
                case 'ID': sortFieldName = 'ID'; break;
                case 'CreationDate': sortFieldName = 'CreationDate'; break;
                case 'StateID': sortFieldName = 'StateID'; break;
                case 'AssignedToEmployeeAgentID': sortFieldName = 'AssignedToEmployeeAgentID'; break;
                case 'AssignedToEmployeeAgentName': sortFieldName = 'AssignedToEmployeeAgentName'; break;
                case 'AuthorName': sortFieldName = 'AuthorName'; break;
                case 'Subject': sortFieldName = 'Subject'; break;
                case 'AssignedDate': sortFieldName = ''; break;
                case 'EstimatedDate': sortFieldName = ''; break;
                case 'SkillID': sortFieldName = ''; break;
                case 'ProjectID': sortFieldName = ''; break;
                case 'ScoreID': sortFieldName = ''; break;
                case 'EstimationHours': sortFieldName = ''; break;
                case 'TrackedHours': sortFieldName = ''; break;
            }*/
            setFilterModel({ ...filterModel, orderBy: `${model[0].field} ${model[0].sort}` });
            
        } else {
            setFilterModel({ ...filterModel, orderBy: undefined });
        }
    }

    return (
        <Paper className='issue-list' sx={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}>
            {filterShowing ?
                <>
                    <Box className="issue-list-aside">
                        <IssueListFilter initialFilter={filterModel} onFilterClosed={() => setFilterShowing(false)} onFilterChanged={filter => setFilterModel({ ...filter, meMode: filterModel.meMode })} />
                    </Box>
                    <Divider orientation="vertical" flexItem />
                </>
                : ''
            }
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', }}>
                <Box className="issue-list-filter-aria">
                    <ToggleButton
                        value="check"
                        className="extended-filter-toggler"
                        size='small'
                        selected={filterShowing}
                        onChange={() => setFilterShowing(!filterShowing)}
                        sx={{mr:2}}
                    >
                        <FilterAltOutlinedIcon />
                    </ToggleButton>

                    <span className="me-mode">
                        Мои
                        <Switch
                            sx={{ ml: 0.5, mr: 1 }}
                            {...{ inputProps: { 'aria-label': 'Size switch demo' } }}
                            defaultChecked
                            onChange={(e) => setFilterModel({ ...filterModel, meMode: e.target.checked })}
                            size="small" />
                    </span>

                    <ToggleButtonGroup
                        value={pressedButtons}
                        onChange={handleBtnClick}
                        size='small'
                        aria-label="text formatting"
                        className="issue-state-togglers"
                    >
                        <ToggleButton value="all" aria-label="bold">
                            Все
                        </ToggleButton>
                            <ToggleButton value={IssueState.backlog} aria-label="underlined" className="grid-column-icon state-1">
                            {isMobile ? '' : "Беклог"}
                        </ToggleButton>
                        <ToggleButton value={IssueState.queued} aria-label="underlined" className="grid-column-icon state-2">
                            {isMobile ? '' : "Очередь"}
                        </ToggleButton>
                        <ToggleButton value={IssueState.assigned} aria-label="underlined" className="grid-column-icon state-3">
                            {isMobile ? '' : "Назначенные"}
                        </ToggleButton>
                        <ToggleButton value={IssueState.accepted} aria-label="color" className="grid-column-icon state-4">
                            {isMobile ? '' : "В работе"}
                        </ToggleButton>
                        <ToggleButton value={IssueState.testing} aria-label="color" className="grid-column-icon state-5">
                            {isMobile ? '' : "Тест"}
                        </ToggleButton>
                        <ToggleButton value={IssueState.available} aria-label="color" className="grid-column-icon state-8">
                            {isMobile ? '' : "Готово"}
                        </ToggleButton>
                        <ToggleButton value={IssueState.commentsWaiting & IssueState.criticalWaiting} aria-label="color" className="grid-column-icon state-7">
                            {isMobile ? '' : "Отложены"}
                        </ToggleButton>
                        <ToggleButton value={IssueState.completed} aria-label="color" className="grid-column-icon state-9">
                            {isMobile ? '' : "Выполненные"}
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Box className="issue-text-search-wrapper">
                        <TextField
                            id="search-issue"
                            className="text"
                            onChange={e => setSearchText(e.target.value)}
                            onKeyDown={e => { if (e.key == 'Enter' && (e.target as HTMLInputElement).value.trim() != '') setFilterModel({ ...filterModel, text: searchText }) }}
                            variant='outlined'
                            sx={{ background: theme.palette.background.paper, width: '100%' }}
                            placeholder="поиск по тексту..."
                            size="small"
                        />
                        <IconButton /*disabled={searchText.trim() != ''} */ aria-label="search" sx={{ position: 'absolute', right: 0, cursor: 'pointer' }}>
                            <Tooltip title="Поиск">
                                <SearchIcon onClick={() => setFilterModel({ ...filterModel, text: searchText })} />
                            </Tooltip>
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ flex: '1', position: 'relative' }}>
                    <DataGrid sx={{ position: 'absolute', left: 10, top: 0, right: 0, bottom: 0, }}
                        rows={list}
                        filterMode="server"
                        paginationMode='server'
                        sortingMode='server'
                        showCellVerticalBorder={true}
                        rowCount={recordsCount}
                        density='compact'
                        disableColumnMenu
                        disableDensitySelector
                        slots={isMobile ? {} : { toolbar: GridToolbar }}
                        disableColumnFilter
                        //onFilterModelChange={onFilterChange}
                        //getRowHeight={() => 'auto'}
                        getRowId={(row) => row.ID}
                        columns={columns}
                        onPaginationModelChange={model => setFilterModel({ ...filterModel, offset: (model.page) * 100 })}
                        onSortModelChange={handleSortModelChange}
                        initialState={{
                            columns: {
                                ...columns,
                                columnVisibilityModel: isMobilePortrait
                                    ? {
                                        StateID: false,
                                        PriorityID: false,
                                        IsError: false,
                                        ID: false,
                                        CreationDate: false,
                                        AssignedDate: false,
                                        AcceptanceDate: false,
                                        QueuedDate: false,
                                        EstimatedDate: false,
                                        SkillName: false,
                                        ProjectName: false,
                                        ScoreID: false,
                                        EstimationHours: false,
                                        TrackedHours: false,
                                        ClientName: false,
                                        AuthorAvatar: false,
                                        AssignedToEmployeeAgentID: false,
                                        AuthorName: false,
                                    } : {
                                        StateID: false,
                                        AssignedToEmployeeAgentID: false,
                                        AuthorName: false,
                                    },
                            },
                            pagination: { paginationModel }
                        }}
                        //pageSizeOptions={[50, 10]}
                        // checkboxSelection
                    />
                </Box>
            </Box>
        </Paper>
    )
});

export default IssuesList;